import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { GenreIcon, YearIcon, DurationIcon, VolumeIcon } from '../icons/MetadataIcons';
import { SpotifyIcon, AppleMusicIcon, YouTubeIcon } from '../icons/SocialIcons';
import { containerStyles, socialMediaStyles } from '../../styles/common';

const styles = stylex.create({
    coverContainer: {
        position: 'relative',
        width: 360,
        height: 360,
        borderRadius: 16,
        marginBottom: '1.5rem',
        overflow: 'hidden',
        boxShadow: '0 4px 32px rgba(0,0,0,0.4)',
        transition: 'transform 0.5s ease, box-shadow 0.5s ease',
        ':hover': {
            transform: 'scale(1.02) translateY(-5px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 20px rgba(255, 120, 50, 0.3)',
        },
        '@media (max-width: 800px)': {
            width: 280,
            height: 280,
        },
    },
    cover: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        background: '#222',
        transition: 'filter 0.5s ease',
        filter: 'contrast(1.1) brightness(1.05)',
        ':hover': {
            filter: 'contrast(1.2) brightness(1.1) saturate(1.1)',
        },
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        background: 'radial-gradient(circle at 70% 30%, rgba(255, 120, 50, 0.15), transparent 70%)',
        mixBlendMode: 'overlay',
        transition: 'opacity 0.8s ease',
        pointerEvents: 'none',
    },
    overlayVisible: {
        opacity: 0.7,
    },
    smoke: {
        position: 'absolute',
        top: '30%',
        left: '10%',
        width: '80%',
        height: '70%',
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.08), transparent 60%)',
        opacity: 0,
        filter: 'blur(20px)',
        transform: 'translateY(0)',
        transition: 'opacity 1s ease, transform 15s ease',
        pointerEvents: 'none',
        mixBlendMode: 'soft-light',
    },
    smokeVisible: {
        opacity: 0.7,
        transform: 'translateY(-20px)',
    },
    glint: {
        position: 'absolute',
        width: '120%',
        height: '10px',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
        top: '-100%',
        left: '-10%',
        transform: 'rotate(45deg)',
        transition: 'top 0.5s ease',
        pointerEvents: 'none',
    },
    glintVisible: {
        top: '150%',
    },
    grain: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        backgroundSize: '100px 100px',
        background: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        pointerEvents: 'none',
        mixBlendMode: 'overlay',
        transition: 'opacity 0.5s ease',
    },
    grainVisible: {
        opacity: 0.05,
    },
    songTitle: {
        fontSize: '1.8rem',
        margin: '0 0 0.5rem 0',
        color: '#fff',
        fontWeight: 300,
        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
        fontFamily: "'Oswald', sans-serif",
    },
    metadata: {
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        marginBottom: '1rem',
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '0.9rem',
        fontFamily: "'Montserrat', sans-serif",
    },
    metadataItem: {
        padding: '0.2rem 0.5rem',
        borderRadius: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.3rem',
    },
    credits: {
        fontSize: '0.9rem',
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
        marginTop: '2rem',
        fontFamily: "'Montserrat', sans-serif",
    },
    creditLink: {
        color: 'rgba(255, 255, 255, 0.6)',
        textDecoration: 'none',
        transition: 'color 0.3s ease',
        ':hover': {
            color: '#fff',
            textDecoration: 'underline',
        },
    },
});

export default function AlbumSection() {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [showGlint, setShowGlint] = useState(false);
    const glintTimerRef = useRef<number | null>(null);

    // Trigger glint effect occasionally when hovered
    useEffect(() => {
        if (isHovered) {
            // Clear existing timers
            if (glintTimerRef.current) clearTimeout(glintTimerRef.current);

            // Trigger glint effect after a random delay
            glintTimerRef.current = window.setTimeout(() => {
                setShowGlint(true);

                // Hide glint after animation
                setTimeout(() => {
                    setShowGlint(false);

                    // Schedule another glint if still hovered
                    if (isHovered) {
                        glintTimerRef.current = window.setTimeout(() => {
                            setShowGlint(true);
                            setTimeout(() => setShowGlint(false), 500);
                        }, Math.random() * 3000 + 2000);
                    }
                }, 500);
            }, Math.random() * 1500 + 1000);
        } else {
            // Clean up timer when not hovered
            if (glintTimerRef.current) {
                clearTimeout(glintTimerRef.current);
                glintTimerRef.current = null;
            }
            setShowGlint(false);
        }

        return () => {
            if (glintTimerRef.current) {
                clearTimeout(glintTimerRef.current);
            }
        };
    }, [isHovered]);

    return (
        <>
            <div
                {...stylex.props(styles.coverContainer)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <img
                    id="albumCover"
                    src="/assets/cover-optimized.jpg"
                    alt="Too Many Mornings Album Cover"
                    {...stylex.props(styles.cover)}
                />

                <div
                    {...stylex.props(
                        styles.overlay,
                        isHovered && styles.overlayVisible
                    )}
                />
                <div
                    {...stylex.props(
                        styles.smoke,
                        isHovered && styles.smokeVisible
                    )}
                />
                <div
                    {...stylex.props(
                        styles.glint,
                        showGlint && styles.glintVisible
                    )}
                />
                <div
                    {...stylex.props(
                        styles.grain,
                        isHovered && styles.grainVisible
                    )}
                />
            </div>
            <h2 id="songTitle" {...stylex.props(styles.songTitle)}>{t('song.title')}</h2>
            <div {...stylex.props(styles.metadata)}>
                <span {...stylex.props(styles.metadataItem)}>
                    <GenreIcon />
                    {t('song.metadata.genre')}
                </span>
                <span {...stylex.props(styles.metadataItem)}>
                    <YearIcon />
                    {t('song.metadata.year')}
                </span>
                <span {...stylex.props(styles.metadataItem)}>
                    <DurationIcon />
                    {t('song.metadata.duration')}
                </span>
                <span {...stylex.props(styles.metadataItem)}>
                    <VolumeIcon />
                    Single
                </span>
            </div>

            <div {...stylex.props(socialMediaStyles.socialLinks)}>
                <a
                    href="https://open.spotify.com/track/0oUJK0WAAdzDesLW5jeT0b?si=efa8866f1d964df5"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...stylex.props(socialMediaStyles.socialLink, socialMediaStyles.spotifyHover)}
                    aria-label="Listen on Spotify"
                >
                    <SpotifyIcon />
                </a>
                <a
                    href="https://music.apple.com/pl/album/this-is-not-our-love/1795983792?i=1795983794"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...stylex.props(socialMediaStyles.socialLink, socialMediaStyles.appleMusicHover)}
                    aria-label="Listen on Apple Music"
                >
                    <AppleMusicIcon />
                </a>
                <a
                    href="https://www.youtube.com/watch?v=FhlolIXYbnU&ab_channel=Release-Topic"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...stylex.props(socialMediaStyles.socialLink, socialMediaStyles.youtubeHover)}
                    aria-label="Listen on YouTube"
                >
                    <YouTubeIcon />
                </a>
            </div>

            <div id="creditsSection" {...stylex.props(containerStyles.simpleContainer, styles.credits)}>
                <p>

                    {t('credits.artist')}, {t('credits.composer')}, {t('credits.lyricist')}: Michał Rudol

                </p>
                <p>
                    {t('credits.producer')}: Jacek Jurewicz
                </p>
            </div>
        </>
    );
} 